import Badge from "@/components/core/badge";
import { Button } from "@/components/core/buttons/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/core/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs";
import Tooltip from "@/components/core/tooltip";
import { CancelInvoiceModal } from "@/components/invoices/cancel-invoice-modal";
import { CollectionReceiptTab } from "@/components/invoices/collection-receipt-tab";
import { RequestJournalItemsTab } from "@/components/invoices/request-journal-items-tab";
import { Content } from "@/components/layouts/content";
import {
	invoiceRequestQueryOptions,
	useInvoiceRequestCorrectionsMutation,
	useInvoiceRequestNotifyClientMutation,
	useInvoiceRequestPaymentBreakdownMutation,
	useInvoiceRequestUploadAttachmentMutation,
} from "@/features/dashboard/api/queries/invoice-request-query";
import type { InvoiceRequest } from "@/features/dashboard/types/invoice-request";
import { queryClient } from "@/query-client";
import { formatNumber } from "@/utils/format-number";
import * as Dialog from "@radix-ui/react-dialog";
import {
	IconArrowLeft,
	IconBuilding,
	IconCalendar,
	IconCash,
	IconCheck,
	IconCurrencyPeso,
	IconDownload,
	IconEdit,
	IconFile,
	IconFileTypePdf,
	IconMail,
	IconPaperclip,
	IconPhone,
	IconPlus,
	IconReceipt,
	IconSend,
	IconTrash,
	IconUpload,
	IconUser,
	IconX,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_dashboard/invoice-requests_/$invoiceRequestId")({
	component: InvoiceRequestDetailPage,
});

const statusColors: Record<string, "yellow-light" | "green-light" | "red-light" | "blue-light" | "purple-light" | "gray-light"> = {
	pending: "yellow-light",
	approved: "blue-light",
	processed: "green-light",
	rejected: "red-light",
};

const paymentStatusColors: Record<string, "yellow-light" | "green-light" | "red-light" | "gray-light" | "blue-light"> = {
	unpaid: "yellow-light",
	pending: "yellow-light",
	partial: "blue-light",
	paid: "green-light",
	overdue: "red-light",
};

function InvoiceRequestDetailPage() {
	const { invoiceRequestId } = Route.useParams();
	const requestQuery = useQuery(invoiceRequestQueryOptions(invoiceRequestId));
	const data = requestQuery.data;
	const [isNotifyOpen, setIsNotifyOpen] = useState(false);
	const [isCancelOpen, setIsCancelOpen] = useState(false);

	// Cancellation availability — three states:
	//   1. canCancel: button enabled (invoice exists + posted JE + not already cancelled)
	//   2. cancelBlocked: button shown but disabled with tooltip (invoice exists but JE is draft/missing)
	//   3. neither: button hidden (no invoice yet, or already cancelled)
	const inv = data?.invoice;
	const isAlreadyCancelled = inv?.is_cancelled ?? false;
	const hasJe = Boolean(inv?.ar_journal_entry_status);
	const isJePosted = inv?.ar_journal_entry_status === "posted";
	const canCancel = Boolean(inv) && isJePosted && !isAlreadyCancelled;
	const cancelBlocked = Boolean(inv) && !isJePosted && !isAlreadyCancelled;
	const cancelBlockedReason = !hasJe
		? "Post this invoice to Books first (Journal Items → Post to Books)."
		: "The journal entry in Books is still a draft. Post it first (Re-send to Books).";
	const hasCollection = data?.payment_status === "paid" || data?.payment_status === "partial";

	if (requestQuery.isLoading) {
		return (
			<Content pageName="Invoice Request">
				<div className="flex items-center justify-center h-64">
					<div className="text-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
						<p className="mt-2 text-sm text-gray-600">Loading...</p>
					</div>
				</div>
			</Content>
		);
	}

	if (!data) {
		return (
			<Content pageName="Invoice Request">
				<div className="flex items-center justify-center h-64">
					<p className="text-lg text-gray-600">Invoice request not found</p>
				</div>
			</Content>
		);
	}

	return (
		<Content pageName="Invoice Request">
			<div className="p-6 space-y-6">
				{/* Header */}
				<div className="flex items-start justify-between">
					<div className="flex items-center gap-3">
						<Link to="/invoice-requests" className="text-gray-500 hover:text-gray-700 transition-colors">
							<IconArrowLeft className="w-5 h-5" />
						</Link>
						<div>
							<div className="flex items-center gap-3">
								<h1 className="text-2xl font-bold text-gray-900">{data.client_name || "Invoice Request"}</h1>
								<Badge size="xs" color={statusColors[data.status] || "gray-light"}>
									{data.status?.charAt(0).toUpperCase() + data.status?.slice(1)}
								</Badge>
								{data.payment_status && (
									<Badge size="xs" color={paymentStatusColors[data.payment_status] || "gray-light"}>
										{data.payment_status.charAt(0).toUpperCase() + data.payment_status.slice(1)}
									</Badge>
								)}
								{isAlreadyCancelled && (
									<Badge size="xs" color="red-light">
										Cancelled
									</Badge>
								)}
							</div>
							<p className="text-sm text-gray-500 mt-1">{data.project_name || data.project?.name || ""}</p>
						</div>
					</div>
					<div className="flex gap-2">
						{data.contact_email && (
							<Button variant="ghost" onClick={() => setIsNotifyOpen(true)}>
								<IconSend size={18} stroke={2} />
								<span>Notify Client</span>
							</Button>
						)}
						{data.id && (
							<Button variant="ghost" onClick={() => window.open(`/api/invoice-requests/${data.id}/generate-document-pdf`, "_blank")}>
								<IconFileTypePdf size={18} stroke={2} />
								<span>Download PDF</span>
							</Button>
						)}
						{canCancel && (
							<Button variant="danger" onClick={() => setIsCancelOpen(true)}>
								<IconX size={18} stroke={2} />
								<span>Cancel invoice</span>
							</Button>
						)}
						{cancelBlocked && (
							<Tooltip content={cancelBlockedReason}>
								{/* Wrap the disabled button in a span so the tooltip's pointer events fire — disabled buttons swallow events in most browsers */}
								<span tabIndex={0} className="inline-block cursor-not-allowed">
									<Button variant="ghost" disabled style={{ pointerEvents: "none" }}>
										<IconX size={18} stroke={2} />
										<span>Cancel invoice</span>
									</Button>
								</span>
							</Tooltip>
						)}
					</div>
				</div>
				{isAlreadyCancelled && inv?.cancellation_reason && (
					<div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">
						<strong>Cancelled:</strong> {inv.cancellation_reason}
					</div>
				)}

				{/* Summary Cards */}
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
					<InfoCard
						icon={<IconCurrencyPeso className="w-5 h-5" />}
						label="Total Amount Due"
						value={data.total_amount_due ? `₱${formatNumber(Number(data.total_amount_due))}` : "-"}
					/>
					<InfoCard icon={<IconCalendar className="w-5 h-5" />} label="Invoice Date Requested" value={formatDate(data.invoice_date_requested)} />
					<InfoCard icon={<IconReceipt className="w-5 h-5" />} label="Receipt Number" value={data.receipt_number || "-"} />
					<InfoCard icon={<IconCash className="w-5 h-5" />} label="Payment Due" value={formatDate(data.payment_due_date)} />
				</div>

				{/* Detail Cards */}
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					{/* Client & Invoice Info */}
					<Card>
						<CardHeader>
							<CardTitle className="text-lg">Client & Invoice</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="space-y-4">
								<DetailItem label="Client Name" value={data.client_name || "-"} />
								<DetailItem label="Client Address" value={data.client_address || "-"} />
								<DetailItem label="TIN No." value={data.tin_no || "-"} />
								<DetailItem label="Invoice Type" value={data.invoice_type ? data.invoice_type.charAt(0).toUpperCase() + data.invoice_type.slice(1) : "-"} />
								<DetailItem label="Billing Type" value={formatBillingType(data.billing_type)} />
								<DetailItem label="Payment Terms" value={data.payment_terms || "-"} />
								{data.contract_duration_months && <DetailItem label="Contract Duration" value={`${data.contract_duration_months} month(s)`} />}
							</div>
						</CardContent>
					</Card>

					{/* Financial Details */}
					<Card>
						<CardHeader>
							<CardTitle className="text-lg">Financial Details</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="space-y-4">
								{data.amount_one_time != null && Number(data.amount_one_time) > 0 && (
									<DetailItem label="One-Time Amount" value={`₱${formatNumber(Number(data.amount_one_time))}`} />
								)}
								{data.amount_recurring != null && Number(data.amount_recurring) > 0 && (
									<DetailItem label="Recurring Amount" value={`₱${formatNumber(Number(data.amount_recurring))}`} />
								)}
								{data.amount_mixed != null && Number(data.amount_mixed) > 0 && (
									<DetailItem label="Mixed Amount" value={`₱${formatNumber(Number(data.amount_mixed))}`} />
								)}
								<DetailItem label="Vatable Sales" value={data.vatable_sales != null ? `₱${formatNumber(Number(data.vatable_sales))}` : "-"} />
								<DetailItem label="VAT Amount" value={data.vat_amount != null ? `₱${formatNumber(Number(data.vat_amount))}` : "-"} />
								<DetailItem label="Total Amount Due" value={data.total_amount_due != null ? `₱${formatNumber(Number(data.total_amount_due))}` : "-"} />
							</div>
						</CardContent>
					</Card>

					{/* Contact & Payment Info */}
					<Card>
						<CardHeader>
							<CardTitle className="text-lg">Contact & Payment</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="space-y-4">
								<DetailItem label="Contact Person" value={data.contact_person || "-"} />
								<DetailItem label="Contact Number" value={data.contact_number || "-"} />
								<DetailItem label="Contact Email" value={data.contact_email || "-"} />
								<DetailItem label="Requested By" value={data.requested_by_user?.name || "-"} />
								<DetailItem label="Request Date" value={formatDate(data.request_date)} />
								{(data.payment_status === "paid" || data.payment_status === "partial") && (
									<>
										<DetailItem label="Amount Collected" value={data.payment_amount != null ? `₱${formatNumber(Number(data.payment_amount))}` : "-"} />
										{data.payment_status === "partial" && (
											<DetailItem label="Remaining Balance" value={`₱${formatNumber(Number(data.total_amount_due || 0) - Number(data.payment_amount || 0))}`} />
										)}
										<DetailItem label="Payment Date" value={formatDate(data.payment_date)} />
										<DetailItem label="Payment Method" value={data.payment_method || "-"} />
										<DetailItem label="Reference Number" value={data.payment_reference_number || "-"} />
									</>
								)}
							</div>
						</CardContent>
					</Card>
				</div>

				{/* Line Items / Journal Items / Collection Receipt (Odoo-style tabs) */}
				<Card>
					<CardContent className="pt-6">
						<Tabs defaultValue="lines">
							<TabsList>
								<TabsTrigger value="lines">Invoice Lines</TabsTrigger>
								<TabsTrigger value="journal">Journal Items</TabsTrigger>
								{(data.payment_status === "paid" || data.payment_status === "partial") && <TabsTrigger value="collection">Collection Receipt</TabsTrigger>}
							</TabsList>
							<TabsContent value="lines" className="pt-4">
								<LineItemsSection data={data} onSaved={() => requestQuery.refetch()} embed />
							</TabsContent>
							<TabsContent value="journal" className="pt-4">
								{data.id ? <RequestJournalItemsTab requestId={data.id} /> : null}
							</TabsContent>
							{(data.payment_status === "paid" || data.payment_status === "partial") && (
								<TabsContent value="collection" className="pt-4">
									{data.id ? <CollectionReceiptTab requestId={data.id} /> : null}
								</TabsContent>
							)}
						</Tabs>
					</CardContent>
				</Card>

				{/* Payment Breakdown */}
				{(data.payment_status === "paid" || data.payment_status === "partial") && (
					<PaymentBreakdownSection data={data} onSaved={() => requestQuery.refetch()} />
				)}

				{/* Notes */}
				{(data.notes || data.follow_up_notes) && (
					<Card>
						<CardHeader>
							<CardTitle className="text-lg">Notes</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="space-y-4">
								{data.notes && (
									<div>
										<p className="text-xs text-gray-500 font-medium">Notes</p>
										<p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">{data.notes}</p>
									</div>
								)}
								{data.follow_up_notes && (
									<div>
										<p className="text-xs text-gray-500 font-medium">Follow-up Notes</p>
										<p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">{data.follow_up_notes}</p>
									</div>
								)}
							</div>
						</CardContent>
					</Card>
				)}

				{/* Attachments */}
				<AttachmentsSection data={data} onUploaded={() => requestQuery.refetch()} />

				{/* Related Links */}
				{(data.sales_contract_id || data.project_id || data.invoice_id) && (
					<Card>
						<CardHeader>
							<CardTitle className="text-lg">Related</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="flex flex-wrap gap-3">
								{data.sales_contract_id && (
									<a
										href={`/sales-contracts/${data.sales_contract_id}`}
										target="_blank"
										rel="noreferrer"
										className="inline-flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-700 transition-colors"
									>
										<IconFile size={16} className="text-gray-500" />
										<span>Sales Contract{data.sales_contract ? `: ${data.sales_contract.project_name}` : ""}</span>
									</a>
								)}
								{data.project_id && (
									<a
										href={`/projects/${data.project_id}`}
										target="_blank"
										rel="noreferrer"
										className="inline-flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-700 transition-colors"
									>
										<IconBuilding size={16} className="text-gray-500" />
										<span>Project{data.project ? `: ${data.project.name}` : ""}</span>
									</a>
								)}
								{data.invoice_id && (
									<a
										href={`/invoices/${data.invoice_id}`}
										target="_blank"
										rel="noreferrer"
										className="inline-flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-700 transition-colors"
									>
										<IconReceipt size={16} className="text-gray-500" />
										<span>Invoice{data.invoice ? `: ${data.invoice.invoice_number}` : ""}</span>
									</a>
								)}
							</div>
						</CardContent>
					</Card>
				)}
			</div>

			{/* Notify Client Dialog */}
			{data.id && (
				<NotifyClientDialog
					invoiceRequestId={data.id}
					contactEmail={data.contact_email || ""}
					clientName={data.client_name || ""}
					projectName={data.project_name || data.project?.name || ""}
					invoiceNumber={data.invoice?.invoice_number}
					totalAmount={data.total_amount_due != null ? Number(data.total_amount_due) : undefined}
					isOpen={isNotifyOpen}
					onClose={() => setIsNotifyOpen(false)}
				/>
			)}

			{/* Cancel Invoice Modal — keyed on the underlying Invoice, not the InvoiceRequest */}
			{inv?.id && (
				<CancelInvoiceModal
					invoiceId={inv.id}
					invoiceNumber={inv.invoice_number}
					hasCollection={hasCollection}
					isOpen={isCancelOpen}
					onClose={() => setIsCancelOpen(false)}
				/>
			)}
		</Content>
	);
}

type LineItem = {
	id?: string | null;
	quantity: number;
	unit_of_measurement: string;
	item_description: string;
	unit_price: number;
	total_price: number;
};

const emptyItem: LineItem = {
	quantity: 1,
	unit_of_measurement: "LOT",
	item_description: "",
	unit_price: 0,
	total_price: 0,
};

function LineItemsSection({ data, onSaved, embed = false }: { data: InvoiceRequest; onSaved: () => void; embed?: boolean }) {
	const [items, setItems] = useState<LineItem[]>([]);
	const [editingIndex, setEditingIndex] = useState<number | null>(null);
	const [isAdding, setIsAdding] = useState(false);
	const [newItem, setNewItem] = useState<LineItem>({ ...emptyItem });
	const correctionsMutation = useInvoiceRequestCorrectionsMutation();

	const sourceItems: LineItem[] =
		data.items && data.items.length > 0
			? data.items.map((item) => ({
					id: item.id,
					quantity: Number(item.quantity),
					unit_of_measurement: item.unit_of_measurement,
					item_description: item.item_description,
					unit_price: Number(item.unit_price),
					total_price: Number(item.total_price),
				}))
			: getDefaultLineItems(data);

	const hasUnsavedDefaults = (!data.items || data.items.length === 0) && sourceItems.length > 0;
	const displayItems = items.length > 0 ? items : sourceItems;

	const saveItems = async (updatedItems: LineItem[]) => {
		if (!data.id) return;
		try {
			await correctionsMutation.mutateAsync({
				id: data.id,
				items: updatedItems.map((item) => ({
					quantity: item.quantity,
					unit_of_measurement: item.unit_of_measurement,
					item_description: item.item_description,
					unit_price: item.unit_price,
					total_price: item.quantity * item.unit_price,
				})),
			});
			await queryClient.invalidateQueries({ queryKey: ["invoice-request", data.id] });
			toast.success("Line items updated");
			setItems([]);
			setEditingIndex(null);
			onSaved();
		} catch {
			toast.error("Failed to update line items");
		}
	};

	const handleDelete = (index: number) => {
		const updated = displayItems.filter((_, i) => i !== index);
		saveItems(updated);
	};

	const handleEditStart = (index: number) => {
		setItems([...sourceItems]);
		setEditingIndex(index);
	};

	const handleEditSave = () => {
		if (editingIndex === null) return;
		const updated = [...items];
		updated[editingIndex] = {
			...updated[editingIndex],
			total_price: updated[editingIndex].quantity * updated[editingIndex].unit_price,
		};
		saveItems(updated);
	};

	const handleEditCancel = () => {
		setItems([]);
		setEditingIndex(null);
	};

	const updateEditField = (field: keyof LineItem, value: string | number) => {
		setItems((prev) => {
			const updated = [...prev];
			if (editingIndex !== null) {
				updated[editingIndex] = { ...updated[editingIndex], [field]: value };
			}
			return updated;
		});
	};

	const handleAddSave = () => {
		if (!newItem.item_description.trim()) return;
		const itemToAdd = { ...newItem, total_price: newItem.quantity * newItem.unit_price };
		const updated = [...sourceItems, itemToAdd];
		saveItems(updated);
		setIsAdding(false);
		setNewItem({ ...emptyItem });
	};

	const handleAddCancel = () => {
		setIsAdding(false);
		setNewItem({ ...emptyItem });
	};

	const body = (
		<>
			<div className="overflow-x-auto">
				<table className="w-full text-sm">
					<thead>
						<tr className="border-b border-gray-200">
							<th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">#</th>
							<th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Description</th>
							<th className="text-right py-2 px-3 text-xs font-medium text-gray-500 uppercase">Qty</th>
							<th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Unit</th>
							<th className="text-right py-2 px-3 text-xs font-medium text-gray-500 uppercase">Unit Price</th>
							<th className="text-right py-2 px-3 text-xs font-medium text-gray-500 uppercase">Total</th>
							<th className="w-[80px] py-2 px-3" />
						</tr>
					</thead>
					<tbody>
						{displayItems.map((item, index) =>
							editingIndex === index ? (
								<tr key={item.id || index} className="border-b border-gray-100">
									<td className="py-2 px-3 text-gray-500">{index + 1}</td>
									<td className="py-2 px-3">
										<input
											type="text"
											value={items[index].item_description}
											onChange={(e) => updateEditField("item_description", e.target.value)}
											className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
										/>
									</td>
									<td className="py-2 px-3">
										<input
											type="number"
											value={items[index].quantity}
											onChange={(e) => updateEditField("quantity", Number(e.target.value))}
											className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right"
											step="0.01"
										/>
									</td>
									<td className="py-2 px-3">
										<input
											type="text"
											value={items[index].unit_of_measurement}
											onChange={(e) => updateEditField("unit_of_measurement", e.target.value)}
											className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
										/>
									</td>
									<td className="py-2 px-3">
										<input
											type="number"
											value={items[index].unit_price}
											onChange={(e) => updateEditField("unit_price", Number(e.target.value))}
											className="w-28 px-2 py-1 border border-gray-300 rounded text-sm text-right"
											step="0.01"
										/>
									</td>
									<td className="py-2 px-3 text-right font-medium text-gray-900">₱{formatNumber(items[index].quantity * items[index].unit_price)}</td>
									<td className="py-2 px-3">
										<div className="flex gap-1 justify-end">
											<button type="button" onClick={handleEditSave} className="p-1 text-green-600 hover:bg-green-50 rounded" title="Save">
												<IconCheck size={16} stroke={2} />
											</button>
											<button type="button" onClick={handleEditCancel} className="p-1 text-gray-400 hover:bg-gray-100 rounded" title="Cancel">
												<IconX size={16} stroke={2} />
											</button>
										</div>
									</td>
								</tr>
							) : (
								<tr key={item.id || index} className="border-b border-gray-100 group">
									<td className="py-2 px-3 text-gray-500">{index + 1}</td>
									<td className="py-2 px-3 text-gray-900">{item.item_description}</td>
									<td className="py-2 px-3 text-right text-gray-900">{item.quantity}</td>
									<td className="py-2 px-3 text-gray-900">{item.unit_of_measurement}</td>
									<td className="py-2 px-3 text-right text-gray-900">₱{formatNumber(item.unit_price)}</td>
									<td className="py-2 px-3 text-right font-medium text-gray-900">₱{formatNumber(item.total_price)}</td>
									<td className="py-2 px-3">
										<div className="flex gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
											<button
												type="button"
												onClick={() => handleEditStart(index)}
												className="p-1 text-blue-600 hover:bg-blue-50 rounded"
												title="Edit"
												disabled={isAdding || editingIndex !== null}
											>
												<IconEdit size={16} stroke={2} />
											</button>
											<button
												type="button"
												onClick={() => handleDelete(index)}
												className="p-1 text-red-500 hover:bg-red-50 rounded"
												title="Delete"
												disabled={isAdding || editingIndex !== null}
											>
												<IconTrash size={16} stroke={2} />
											</button>
										</div>
									</td>
								</tr>
							),
						)}
						{isAdding && (
							<tr className="border-b border-gray-100 bg-blue-50/30">
								<td className="py-2 px-3 text-gray-500">{displayItems.length + 1}</td>
								<td className="py-2 px-3">
									<input
										type="text"
										value={newItem.item_description}
										onChange={(e) => setNewItem((prev) => ({ ...prev, item_description: e.target.value }))}
										className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
										placeholder="Description"
										autoFocus
									/>
								</td>
								<td className="py-2 px-3">
									<input
										type="number"
										value={newItem.quantity}
										onChange={(e) => setNewItem((prev) => ({ ...prev, quantity: Number(e.target.value) }))}
										className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right"
										step="0.01"
									/>
								</td>
								<td className="py-2 px-3">
									<input
										type="text"
										value={newItem.unit_of_measurement}
										onChange={(e) => setNewItem((prev) => ({ ...prev, unit_of_measurement: e.target.value }))}
										className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
									/>
								</td>
								<td className="py-2 px-3">
									<input
										type="number"
										value={newItem.unit_price}
										onChange={(e) => setNewItem((prev) => ({ ...prev, unit_price: Number(e.target.value) }))}
										className="w-28 px-2 py-1 border border-gray-300 rounded text-sm text-right"
										step="0.01"
									/>
								</td>
								<td className="py-2 px-3 text-right font-medium text-gray-900">₱{formatNumber(newItem.quantity * newItem.unit_price)}</td>
								<td className="py-2 px-3">
									<div className="flex gap-1 justify-end">
										<button
											type="button"
											onClick={handleAddSave}
											className="p-1 text-green-600 hover:bg-green-50 rounded"
											title="Save"
											disabled={!newItem.item_description.trim()}
										>
											<IconCheck size={16} stroke={2} />
										</button>
										<button type="button" onClick={handleAddCancel} className="p-1 text-gray-400 hover:bg-gray-100 rounded" title="Cancel">
											<IconX size={16} stroke={2} />
										</button>
									</div>
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
			{displayItems.length === 0 && !isAdding && (
				<div className="text-center py-8 text-gray-500">
					<p className="text-sm">No line items yet.</p>
				</div>
			)}
			{hasUnsavedDefaults && (
				<div className="flex items-center justify-between mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
					<span className="text-sm text-amber-700">These items are auto-generated from the invoice request and have not been saved yet.</span>
					<Button size="sm" onClick={() => saveItems(sourceItems)} disabled={correctionsMutation.isPending}>
						{correctionsMutation.isPending ? "Saving..." : "Save Items"}
					</Button>
				</div>
			)}
		</>
	);

	const addButton = (
		<Button variant="ghost" size="sm" onClick={() => setIsAdding(true)} disabled={isAdding || editingIndex !== null}>
			<IconPlus size={16} stroke={2} />
			<span>Add Item</span>
		</Button>
	);

	if (embed) {
		return (
			<div className="flex flex-col gap-3">
				<div className="flex items-center justify-end">{addButton}</div>
				{body}
			</div>
		);
	}

	return (
		<Card>
			<CardHeader>
				<div className="flex items-center justify-between">
					<CardTitle className="text-lg">Line Items</CardTitle>
					{addButton}
				</div>
			</CardHeader>
			<CardContent>{body}</CardContent>
		</Card>
	);
}

function NotifyClientDialog({
	invoiceRequestId,
	contactEmail,
	clientName,
	projectName,
	invoiceNumber,
	totalAmount,
	isOpen,
	onClose,
}: {
	invoiceRequestId: string;
	contactEmail: string;
	clientName: string;
	projectName: string;
	invoiceNumber?: string;
	totalAmount?: number;
	isOpen: boolean;
	onClose: () => void;
}) {
	const notifyMutation = useInvoiceRequestNotifyClientMutation();
	const [email, setEmail] = useState(contactEmail);
	const [subject, setSubject] = useState("");
	const [message, setMessage] = useState("");

	const resetForm = () => {
		setEmail(contactEmail);
		setSubject(invoiceNumber ? `Invoice ${invoiceNumber} - ${projectName || clientName}` : `Invoice Notification - ${projectName || clientName}`);
		setMessage(
			`Please be informed that we have issued an invoice for ${projectName || "your project"}${totalAmount ? ` amounting to ₱${formatNumber(totalAmount)}` : ""}.\n\nKindly review the details and process the payment at your earliest convenience.\n\nShould you have any questions or require further clarification, please do not hesitate to reach out.`,
		);
	};

	// Reset form when dialog opens
	const handleOpenChange = (open: boolean) => {
		if (open) {
			resetForm();
		} else {
			onClose();
		}
	};

	// Initialize on first open
	if (isOpen && !subject) {
		resetForm();
	}

	const handleSend = async () => {
		if (!email || !subject || !message) return;
		try {
			await notifyMutation.mutateAsync({ id: invoiceRequestId, email, subject, message });
			toast.success(`Notification sent to ${email}`);
			onClose();
		} catch {
			toast.error("Failed to send notification");
		}
	};

	return (
		<Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
				<Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-lg translate-x-[-50%] translate-y-[-50%] border bg-white p-6 shadow-lg rounded-lg">
					<Dialog.Title className="text-lg font-semibold">Notify Client</Dialog.Title>
					<Dialog.Description className="text-sm text-gray-500 mt-1">Send an email notification to the client with invoice details.</Dialog.Description>

					<div className="mt-4 space-y-4">
						<label className="flex flex-col gap-1">
							<span className="text-sm font-medium text-gray-700">To</span>
							<input
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-primary focus:border-primary"
								placeholder="client@email.com"
							/>
						</label>

						<label className="flex flex-col gap-1">
							<span className="text-sm font-medium text-gray-700">Subject</span>
							<input
								type="text"
								value={subject}
								onChange={(e) => setSubject(e.target.value)}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-primary focus:border-primary"
							/>
						</label>

						<label className="flex flex-col gap-1">
							<span className="text-sm font-medium text-gray-700">Message</span>
							<textarea
								value={message}
								onChange={(e) => setMessage(e.target.value)}
								rows={6}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-primary focus:border-primary resize-none"
							/>
							<span className="text-xs text-gray-400">Invoice details and line items will be included automatically.</span>
						</label>
					</div>

					<div className="flex justify-end gap-2 mt-6">
						<Button variant="ghost" onClick={onClose}>
							Cancel
						</Button>
						<Button onClick={handleSend} disabled={!email || !subject || !message || notifyMutation.isPending}>
							<IconSend size={16} stroke={2} />
							<span>{notifyMutation.isPending ? "Sending..." : "Send"}</span>
						</Button>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}

type BreakdownField = {
	key: string;
	label: string;
};

const DEDUCTION_FIELDS: BreakdownField[] = [
	{ key: "deduction_withholding_tax", label: "Withholding Tax" },
	{ key: "deduction_liquidated_damages", label: "Liquidated Damages" },
	{ key: "deduction_retention", label: "Retention" },
	{ key: "deduction_municipal_charge", label: "Municipal Charge" },
	{ key: "deduction_bank_charge", label: "Bank Charge" },
];

function PaymentBreakdownSection({ data, onSaved }: { data: InvoiceRequest; onSaved: () => void }) {
	const [isEditing, setIsEditing] = useState(false);
	const [values, setValues] = useState<Record<string, number | null>>({});
	const breakdownMutation = useInvoiceRequestPaymentBreakdownMutation();

	const totalAmountDue = Number(data.total_amount_due ?? 0);
	const totalDeductions = DEDUCTION_FIELDS.reduce((sum, f) => sum + Number((data as Record<string, unknown>)[f.key] ?? 0), 0);
	const netAmount = data.net_amount != null ? Number(data.net_amount) : totalAmountDue - totalDeductions;

	const editTotalDeductions = DEDUCTION_FIELDS.reduce((sum, f) => sum + (values[f.key] ?? 0), 0);
	const editNetAmount = totalAmountDue - editTotalDeductions;

	const startEdit = () => {
		const initial: Record<string, number | null> = {};
		for (const f of DEDUCTION_FIELDS) {
			const val = (data as Record<string, unknown>)[f.key];
			initial[f.key] = val != null ? Number(val) : 0;
		}
		setValues(initial);
		setIsEditing(true);
	};

	const handleSave = async () => {
		if (!data.id) return;
		try {
			await breakdownMutation.mutateAsync({ id: data.id, ...values } as Parameters<typeof breakdownMutation.mutateAsync>[0]);
			await queryClient.invalidateQueries({ queryKey: ["invoice-request", data.id] });
			toast.success("Payment breakdown updated");
			setIsEditing(false);
			onSaved();
		} catch {
			toast.error("Failed to update payment breakdown");
		}
	};

	const hasDeductions = totalDeductions > 0;

	return (
		<Card>
			<CardHeader>
				<div className="flex items-center justify-between">
					<CardTitle className="text-lg">Payment Breakdown</CardTitle>
					{!isEditing && (
						<Button variant="ghost" size="sm" onClick={startEdit}>
							<IconEdit size={16} stroke={2} />
							<span>Edit</span>
						</Button>
					)}
				</div>
			</CardHeader>
			<CardContent>
				<div className="max-w-lg">
					{/* Total Amount Due */}
					<div className="flex justify-between py-2 border-b border-gray-200">
						<span className="font-semibold text-sm text-gray-900">Total Amount Due</span>
						<span className="font-semibold text-sm text-gray-900">₱{formatNumber(totalAmountDue)}</span>
					</div>

					{/* Deductions */}
					<div className="mt-4">
						<p className="font-semibold text-sm text-gray-900 mb-2">Deductions</p>
						<div className="space-y-2 pl-4">
							{DEDUCTION_FIELDS.map((field) => {
								const val = isEditing ? (values[field.key] ?? 0) : Number((data as Record<string, unknown>)[field.key] ?? 0);
								return (
									<div key={field.key} className="flex justify-between items-center">
										<span className="text-sm text-gray-600">{field.label}</span>
										{isEditing ? (
											<input
												type="number"
												value={val}
												onChange={(e) => setValues((prev) => ({ ...prev, [field.key]: Number(e.target.value) || 0 }))}
												className="w-36 px-2 py-1 border border-gray-300 rounded text-sm text-right"
												step="0.01"
												min="0"
											/>
										) : (
											<span className="text-sm text-gray-900">{val > 0 ? `₱${formatNumber(val)}` : "-"}</span>
										)}
									</div>
								);
							})}
						</div>
					</div>

					{/* Total Deductions */}
					{(isEditing || hasDeductions) && (
						<div className="flex justify-between py-2 mt-2 border-t border-gray-200">
							<span className="text-sm font-medium text-red-600">Total Deductions</span>
							<span className="text-sm font-medium text-red-600">₱{formatNumber(isEditing ? editTotalDeductions : totalDeductions)}</span>
						</div>
					)}

					{/* Net Amount */}
					<div className="flex justify-between py-2 mt-2 border-t-2 border-gray-900">
						<span className="font-bold text-gray-900">Net Amount</span>
						<span className="font-bold text-gray-900">₱{formatNumber(isEditing ? editNetAmount : netAmount)}</span>
					</div>

					{/* Edit Actions */}
					{isEditing && (
						<div className="flex justify-end gap-2 mt-4">
							<Button variant="ghost" size="sm" onClick={() => setIsEditing(false)}>
								Cancel
							</Button>
							<Button size="sm" onClick={handleSave} disabled={breakdownMutation.isPending}>
								{breakdownMutation.isPending ? "Saving..." : "Save"}
							</Button>
						</div>
					)}
				</div>
			</CardContent>
		</Card>
	);
}

function AttachmentsSection({ data, onUploaded }: { data: InvoiceRequest; onUploaded: () => void }) {
	const [isUploading, setIsUploading] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const uploadMutation = useInvoiceRequestUploadAttachmentMutation();

	const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file || !data.id) return;

		setIsUploading(true);
		try {
			await uploadMutation.mutateAsync({ id: data.id, file });
			await queryClient.invalidateQueries({ queryKey: ["invoice-request", data.id] });
			toast.success("Attachment uploaded");
			onUploaded();
		} catch {
			toast.error("Failed to upload attachment");
		} finally {
			setIsUploading(false);
			if (fileInputRef.current) fileInputRef.current.value = "";
		}
	};

	const attachments = data.payment_attachments || [];

	return (
		<Card>
			<CardHeader>
				<div className="flex items-center justify-between">
					<CardTitle className="text-lg">Attachments</CardTitle>
					{(data.payment_status === "paid" || data.payment_status === "partial") && (
						<>
							<input ref={fileInputRef} type="file" accept="image/*,.pdf,.doc,.docx" className="hidden" onChange={handleUpload} />
							<Button variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()} disabled={isUploading}>
								<IconUpload size={16} stroke={2} />
								<span>{isUploading ? "Uploading..." : "Upload"}</span>
							</Button>
						</>
					)}
				</div>
			</CardHeader>
			<CardContent>
				{attachments.length === 0 ? (
					<div className="text-center py-8 text-gray-500">
						<IconPaperclip className="w-10 h-10 mx-auto mb-2 text-gray-300" />
						<p className="text-sm">No attachments yet.</p>
					</div>
				) : (
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
						{attachments.map((att) => (
							<a
								key={att.id}
								href={att.url}
								target="_blank"
								rel="noreferrer"
								className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
							>
								<div className="flex-shrink-0">
									<div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center">
										<IconFile size={20} className="text-gray-500" />
									</div>
								</div>
								<div className="flex-1 min-w-0">
									<p className="text-sm font-medium text-gray-900 truncate">{att.name}</p>
									<p className="text-xs text-gray-500">{formatFileSize(att.size)}</p>
								</div>
								<IconDownload size={16} className="text-gray-400 flex-shrink-0" />
							</a>
						))}
					</div>
				)}
			</CardContent>
		</Card>
	);
}

function InfoCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
	return (
		<Card>
			<CardContent className="p-4">
				<div className="flex items-center gap-3">
					<div className="text-gray-500">{icon}</div>
					<div>
						<p className="text-xs text-gray-500">{label}</p>
						<p className="text-sm font-semibold text-gray-900">{value}</p>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}

function DetailItem({ label, value }: { label: string; value: string }) {
	return (
		<div>
			<p className="text-xs text-gray-500 font-medium">{label}</p>
			<p className="text-sm font-medium text-gray-900 mt-1">{value}</p>
		</div>
	);
}

function formatDate(value?: string | null) {
	if (!value) return "-";
	return new Date(value).toLocaleDateString("en-PH", {
		year: "numeric",
		month: "long",
		day: "numeric",
	});
}

function formatBillingType(value?: string | null) {
	if (!value) return "-";
	const map: Record<string, string> = {
		one_time: "One-Time",
		recurring: "Recurring",
		mixed: "Mixed",
	};
	return map[value] || value;
}

function formatFileSize(bytes: number) {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getDefaultLineItems(data: InvoiceRequest): LineItem[] {
	const amount =
		Number(data.total_amount_due ?? 0) || Number(data.amount_one_time ?? 0) || Number(data.amount_recurring ?? 0) || Number(data.amount_mixed ?? 0);
	const description = data.project_name || data.project?.name || data.client_name || "";
	if (!description && amount === 0) return [];
	return [
		{
			quantity: 1,
			unit_of_measurement: "LOT",
			item_description: description,
			unit_price: amount,
			total_price: amount,
		},
	];
}
